import { useRef } from "react";
import type { ShapeCanvasObject } from "@refinement-canvas/domain";
import { useCanvasStore } from "../store/canvas-store";
import { useObjectDrag } from "../input/use-object-drag";
import { useCanvasOpDispatcherContext } from "../dispatcher-context";

interface Props {
  readonly object: ShapeCanvasObject;
}

// 図形オブジェクト（丸 / 四角 / 矢印）。SVG で描画する。
// クリックで選択、ドラッグで移動できる。SVG 自体に pointer events を付けて
// 内側 fill が transparent でもヒットを取れるよう pointerEvents="bounding-box" を付与する。
export function ShapeObject({ object }: Props): JSX.Element {
  const ref = useRef<SVGSVGElement>(null);
  const dispatcher = useCanvasOpDispatcherContext();
  const activeTool = useCanvasStore((s) => s.tool);
  const isSelected = useCanvasStore((s) =>
    s.selectedObjectIds.length === 1 && s.selectedObjectIds[0] === object.id
  );

  useObjectDrag({
    objectId: object.id,
    elementRef: ref as unknown as React.RefObject<HTMLElement | null>,
    dispatcher,
    initialX: object.x,
    initialY: object.y,
    initialRotation: object.rotation,
    disabled: activeTool === "draw-pen",
  });

  const stroke = isSelected ? "var(--color-accent)" : object.stroke ?? "#1a1a2e";

  return (
    <svg
      ref={ref}
      data-testid={`canvas-object-${object.id}`}
      data-object-type="shape"
      style={{
        position: "absolute",
        left: `${object.x}px`,
        top: `${object.y}px`,
        width: `${object.w}px`,
        height: `${object.h}px`,
        zIndex: object.zOrder,
        transform: object.rotation ? `rotate(${object.rotation}deg)` : undefined,
        transformOrigin: "center",
        pointerEvents: activeTool === "draw-pen" ? "none" : "auto",
        cursor: "move",
        overflow: "visible",
      }}
      viewBox={`0 0 ${object.w} ${object.h}`}
    >
      {object.shapeKind === "circle" ? (
        <ellipse
          cx={object.w / 2}
          cy={object.h / 2}
          rx={object.w / 2 - 2}
          ry={object.h / 2 - 2}
          fill={object.fill ?? "transparent"}
          stroke={stroke}
          strokeWidth={isSelected ? 3 : 2}
          pointerEvents="all"
        />
      ) : object.shapeKind === "rectangle" ? (
        <rect
          x={2}
          y={2}
          width={object.w - 4}
          height={object.h - 4}
          fill={object.fill ?? "transparent"}
          stroke={stroke}
          strokeWidth={isSelected ? 3 : 2}
          pointerEvents="all"
        />
      ) : (
        <g stroke={stroke} fill="none" strokeWidth={isSelected ? 3 : 2} pointerEvents="all">
          <line x1={4} y1={object.h / 2} x2={object.w - 12} y2={object.h / 2} />
          <polyline points={`${object.w - 12},${object.h / 2 - 6} ${object.w - 4},${object.h / 2} ${object.w - 12},${object.h / 2 + 6}`} />
        </g>
      )}
    </svg>
  );
}
